import React, { useState } from 'react';
import { Popover, Button, InputNumber, Space, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface YAxisControlData {
    yAxisMin?: number;
    yAxisMax?: number;
    dataMin?: number;
    dataMax?: number;
    defaultMin: number;
    defaultMax: number;
}

export interface YAxisControlPopoverProps {
    /** Unique identifier for this chart (used for state management) */
    id: string | number;
    /** Current y-axis limits */
    limits: { min: number | null; max: number | null };
    /** Data range information */
    dataRange: YAxisControlData;
    /** Callback when limits are applied */
    onApply: (min: number | null, max: number | null) => void;
    /** Callback when reset is clicked */
    onReset: () => void;
}

export const YAxisControlPopover: React.FC<YAxisControlPopoverProps> = ({
    id,
    limits,
    dataRange,
    onApply,
    onReset,
}) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [tempYAxisMin, setTempYAxisMin] = useState<number | null>(null);
    const [tempYAxisMax, setTempYAxisMax] = useState<number | null>(null);

    const handlePopoverOpenChange = (visible: boolean) => {
        setPopoverVisible(visible);
        if (visible) {
            setTempYAxisMin(limits.min);
            setTempYAxisMax(limits.max);
        }
    };

    const handleApply = () => {
        const tempMin: number | null = tempYAxisMin !== null ? tempYAxisMin : null;
        const tempMax: number | null = tempYAxisMax !== null ? tempYAxisMax : null;
        if (tempMin !== null && tempMax !== null && tempMin >= tempMax) {
            return;
        }
        onApply(tempMin, tempMax);
        setPopoverVisible(false);
    };

    const handleReset = () => {
        onReset();
        setTempYAxisMin(dataRange.defaultMin);
        setTempYAxisMax(dataRange.defaultMax);
        setPopoverVisible(false);
    };

    const currentMin = tempYAxisMin !== null ? tempYAxisMin : limits.min;
    const currentMax = tempYAxisMax !== null ? tempYAxisMax : limits.max;
    const range = dataRange.defaultMax - dataRange.defaultMin;
    const step = range * 0.05 || 0.1;
    const hasError = currentMin !== null && currentMax !== null && currentMin >= currentMax;

    return (
        <Popover
            content={
                <div style={{ width: 280 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Y-Axis Minimum</Text>
                            <InputNumber
                                style={{ width: '100%', marginTop: 4 }}
                                value={currentMin}
                                onChange={(val) => setTempYAxisMin(val !== null ? val : null)}
                                step={step}
                                precision={4}
                                placeholder={`Default: ${dataRange.defaultMin.toFixed(4)}`}
                            />
                            {dataRange.dataMin !== undefined && (
                                <Text type="secondary" style={{ fontSize: '11px', marginTop: 2, display: 'block' }}>
                                    Data min: {dataRange.dataMin.toFixed(4)} (default: {dataRange.defaultMin.toFixed(4)})
                                </Text>
                            )}
                        </div>
                        <div>
                            <Text strong>Y-Axis Maximum</Text>
                            <InputNumber
                                style={{ width: '100%', marginTop: 4 }}
                                value={currentMax}
                                onChange={(val) => setTempYAxisMax(val !== null ? val : null)}
                                step={step}
                                precision={4}
                                placeholder={`Default: ${dataRange.defaultMax.toFixed(4)}`}
                            />
                            {dataRange.dataMax !== undefined && (
                                <Text type="secondary" style={{ fontSize: '11px', marginTop: 2, display: 'block' }}>
                                    Data max: {dataRange.dataMax.toFixed(4)} (default: {dataRange.defaultMax.toFixed(4)})
                                </Text>
                            )}
                        </div>
                        {hasError && (
                            <Text type="danger" style={{ fontSize: '11px' }}>
                                Minimum must be less than maximum
                            </Text>
                        )}
                        <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
                            <Button size="small" onClick={handleReset}>
                                Reset
                            </Button>
                            <Button 
                                type="primary" 
                                size="small" 
                                onClick={handleApply}
                                disabled={hasError}
                            >
                                Apply
                            </Button>
                        </Space>
                    </Space>
                </div>
            }
            title="Y-Axis Limits"
            trigger="click"
            open={popoverVisible}
            onOpenChange={handlePopoverOpenChange}
            placement="bottomRight"
        >
            <Button
                size="small"
                icon={<SettingOutlined />}
                title="Configure Y-axis limits"
            >
                Y-axis
            </Button>
        </Popover>
    );
};
